import type { QueryClient } from "@tanstack/react-query";

// eslint-disable-next-line no-restricted-imports -- navigation must not import the businessObjects barrel
import { parseCollectionQueryEnvelope } from "~/features/businessObjects/utils/collectionQuery";
// eslint-disable-next-line no-restricted-imports -- same cycle as above
import { postParentScopedChildrenQuery } from "~/features/businessObjects/utils/parentScopedChildQuery";

import type { NavPageRecord, NavPageSlotRecord } from "../pageModel/types";
import { schemaOwnersQueryOptions } from "../queryOptions";
import type { SchemaRef } from "../utils/schemaOwner";
import { recordHomeOutcome } from "./recordHomeOutcome";
import { recordHomeParentScope } from "./recordHomeParentScope";
import { chooseRecordHome, findRecordHomeCandidates, ownerLookupsFor } from "./resolveRecordHome";

const PARENT_LOOKUP_LIMIT = 5;

const refKey = (ref: SchemaRef): string => `${ref.schema}@${ref.version}`;

export interface FetchRecordHomeRedirectArgs {
  tenantId: string;
  schema: string;
  version: string;
  objectId: string;
  from: string | undefined;
  pages: readonly NavPageRecord[];
  slots: readonly NavPageSlotRecord[];
}

/**
 * Resolves a record-home URL before navigation (workbench lists, name links). When exactly one
 * parent is found, returns the Material-style host URL with tab and selection params. Otherwise
 * returns null and the caller opens the record's own route (ViewsDetailPage may still redirect).
 */
export const fetchRecordHomeRedirect = async (
  queryClient: QueryClient,
  args: FetchRecordHomeRedirectArgs,
  signal?: AbortSignal,
): Promise<string | null> => {
  const { tenantId, schema, version, objectId, from, pages, slots } = args;
  if (!schema || !version || !objectId) {
    return null;
  }

  const candidates = findRecordHomeCandidates(pages, slots, schema, version);
  if (candidates.length === 0) {
    return null;
  }

  const lookups = ownerLookupsFor(candidates);
  const ownersByKey = new Map<string, SchemaRef[] | undefined>();
  for (const ref of lookups) {
    try {
      const owners = await queryClient.fetchQuery(
        schemaOwnersQueryOptions(ref.schema, ref.version, tenantId),
      );
      ownersByKey.set(refKey(ref), owners);
    } catch {
      return null;
    }
  }

  const home = chooseRecordHome(candidates, (child) => ownersByKey.get(refKey(child)));
  if (!home) {
    return null;
  }

  try {
    const raw = await postParentScopedChildrenQuery(
      recordHomeParentScope(home, schema, version, objectId),
      { limit: PARENT_LOOKUP_LIMIT },
      undefined,
      signal,
    );
    const records = parseCollectionQueryEnvelope(raw).records;
    const outcome = recordHomeOutcome(home, { status: "success", records }, objectId, from);

    return outcome.status === "redirect" ? outcome.to : null;
  } catch {
    return null;
  }
};
